/**
 * Current-user metadata fetched from /api/me.
 *
 * Identity comes from the self-managed session (Google sign-in). The
 * backend returns `is_owner`/`is_master` (the operator account), the
 * Google profile (name/picture), `mode` ("view" | future "live"), and the
 * sign-up profile (phone/country/… + `profile_complete`).
 *
 * /api/me is a PUBLIC probe — it returns 200 either way (it is not
 * guarded), with `authenticated: false` when there's no session. So a
 * signed-out boot fires exactly one clean request, no 401 in the console.
 * `status === "unauthenticated"` (from `authenticated: false`) makes the
 * app redirect to the landing/sign-in site. `profileComplete === false`
 * (non-master) makes the app show the one-time onboarding form first.
 * `roles` + `capabilities` drive RBAC: gate UI with `useCan(cap)` (e.g.
 * analysis.run, trading.read/write, journal.*, dashboard.view). The server
 * enforces each capability too (403); the frontend gate is UX.
 * `chatEnabled` mirrors `features.chat` (TICKVIEW_CHAT_ENABLED): false hides
 * every chat surface. It is false until /api/me says otherwise.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type { AnalyticsPrefsUpdate, MePayload, ProfileUpdate } from "@/api/types";

interface MeState {
  /** Stable user id — what chat's sender / supporter ids refer to. */
  sub: string;
  email: string;
  name: string;
  picture: string;
  /** RBAC roles (empty = viewer) + the union of their capabilities. Gate
   *  UI with `useCan(cap)`; the admin role replaces the old `isOwner`
   *  boolean. */
  roles: string[];
  capabilities: string[];
  mode: string;
  // Sign-up profile.
  phone: string;
  country: string;
  timezone: string;
  experience: string;
  referralSource: string;
  /** false → app shows the onboarding form first (always true for master). */
  profileComplete: boolean;
  /** Guided tours already finished or skipped by this user. */
  toursSeen: string[];
  /** Usage analytics: on by default, the user can turn it off. */
  analyticsOptOut: boolean;
  /** false → show the one-time usage-data notice. */
  analyticsNoticeSeen: boolean;
  /** Server flag `features.chat`: chat panel, socket and inbox exist only
   *  when true. */
  chatEnabled: boolean;
  status: "idle" | "loading" | "succeeded" | "failed" | "unauthenticated";
  error: string | null;
}

const initialState: MeState = {
  sub: "",
  email: "",
  name: "",
  picture: "",
  roles: [],
  capabilities: [],
  mode: "view",
  phone: "",
  country: "",
  timezone: "",
  experience: "",
  referralSource: "",
  profileComplete: false,
  toursSeen: [],
  analyticsOptOut: false,
  analyticsNoticeSeen: true,
  chatEnabled: false,
  status: "idle",
  error: null,
};

/** Copy an authenticated /api/me (or /api/profile) payload into state. */
function applyAuthed(state: MeState, p: MePayload): void {
  state.sub = p.sub ?? "";
  state.email = p.email ?? "";
  state.name = p.name ?? "";
  state.picture = p.picture ?? "";
  state.roles = p.roles ?? [];
  state.capabilities = p.capabilities ?? [];
  state.mode = p.mode ?? "view";
  state.phone = p.phone ?? "";
  state.country = p.country ?? "";
  state.timezone = p.timezone ?? "";
  state.experience = p.experience ?? "";
  state.referralSource = p.referral_source ?? "";
  state.profileComplete = p.profile_complete ?? false;
  state.toursSeen = p.tours_seen ?? [];
  state.analyticsOptOut = p.analytics_opt_out ?? false;
  state.analyticsNoticeSeen = p.analytics_notice_seen ?? true;
  state.chatEnabled = p.features?.chat ?? false;
}

export const fetchMe = createAsyncThunk<MePayload, void>(
  "me/fetch",
  async () => await api.getMe(),
);

/** Submit the onboarding profile; the response is a refreshed /api/me
 *  (profile_complete: true), so we apply it like a fetchMe. */
export const saveProfile = createAsyncThunk<MePayload, ProfileUpdate>(
  "me/saveProfile",
  async (payload) => await api.saveProfile(payload),
);

/** Change the usage-analytics opt-out and/or acknowledge the notice. Applied
 *  optimistically so the switch and the notice respond at once. */
export const updateAnalyticsPrefs = createAsyncThunk<MePayload, AnalyticsPrefsUpdate>(
  "me/updateAnalyticsPrefs",
  async (body) => await api.setAnalyticsPrefs(body),
);

const meSlice = createSlice({
  name: "me",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMe.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        if (!action.payload.authenticated) {
          // No session — redirect to the landing/sign-in. Clear any
          // stale profile.
          state.status = "unauthenticated";
          state.error = null;
          applyAuthed(state, { authenticated: false });
          return;
        }
        state.status = "succeeded";
        applyAuthed(state, action.payload);
      })
      .addCase(fetchMe.rejected, (state, action) => {
        // Only real failures land here now (network / 5xx) — /api/me no
        // longer 401s. Fail closed.
        state.status = "failed";
        state.error = action.error.message ?? "Failed to fetch /api/me";
        state.roles = [];
        state.capabilities = [];
        state.chatEnabled = false;
      })
      .addCase(saveProfile.fulfilled, (state, action) => {
        // profile_complete is now true → the onboarding gate releases.
        applyAuthed(state, action.payload);
      })
      .addCase(updateAnalyticsPrefs.pending, (state, action) => {
        const { opt_out, notice_seen } = action.meta.arg;
        if (opt_out !== undefined) state.analyticsOptOut = opt_out;
        if (notice_seen) state.analyticsNoticeSeen = true;
      })
      .addCase(updateAnalyticsPrefs.fulfilled, (state, action) => {
        applyAuthed(state, action.payload);
      })
      .addCase(updateAnalyticsPrefs.rejected, (state, action) => {
        // The switch reverts; a dismissed notice stays dismissed for this
        // session and comes back on the next load.
        const { opt_out } = action.meta.arg;
        if (opt_out !== undefined) state.analyticsOptOut = !opt_out;
      });
  },
});

export default meSlice.reducer;
